import { useCallback, useDeferredValue, useState, useMemo } from 'react';
import { TaskToDoEntry, TaskTree, useGraphneAPI } from '@nexelem/graphne-api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { filtersMapper } from '../mappers';
import { useCustomToast } from '@nexelem/snackbar';
import { generateKebabCase } from '@nexelem/utils';
import { FilterDataTyped } from '@nexelem/filters';
import { DrawerFiltersData } from '../components/TasksDrawerFilters';
import { TaskViewType } from '../types';
import { useFlatTasksListStore, useTreeTasksListStore } from '../../../../ToDoList';
import { useCustomTranslation } from '../../../../../../hooks';
import { useConfig } from '@nexelem/config';
import { useSimulationId } from '../../../GanttSimulation/hooks';

type useTasksViewProps = {
  isTreeViewEnabled: boolean;
  filters: FilterDataTyped<DrawerFiltersData>;
};

type TaskView<T> = {
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  tasksList: T[];
  checkTasksIds: number[];
  totalTasks: number;
  handleTaskCheck: (taskId: string) => void;
  handleCheckAll: (checked: boolean) => void;
  onCheckAll: (value: boolean) => void;
  isCheckAllFilterChecked: boolean;
  queryKey: string;
};

type FlatTaskView = TaskView<TaskToDoEntry> & {
  taskViewType: TaskViewType.FLAT;
};

type TreeTaskView = TaskView<TaskTree> & {
  taskViewType: TaskViewType.TREE;
};

export const useTasksView = ({ isTreeViewEnabled, filters }: useTasksViewProps): FlatTaskView | TreeTaskView => {
  const graphneAPI = useGraphneAPI();
  const displayToast = useCustomToast();
  const { countOptionsFetchForSelect } = useConfig();
  const { tWrapper } = useCustomTranslation('components.toasts.commonMessage');
  const [isCheckAllFilterChecked, setIsCheckAllFilterChecked] = useState(false);
  const simulationId = useSimulationId();

  const queryCommonKey = [JSON.stringify(filters), simulationId.id ? simulationId.id : undefined];
  const flatListQueryKey = generateKebabCase('to-do-flat-list', ...queryCommonKey);
  const treeListQueryKey = generateKebabCase('to-do-tree-list', ...queryCommonKey);

  const deferredFlatListQueryKey = useDeferredValue(flatListQueryKey);
  const deferredTreeListQueryKey = useDeferredValue(treeListQueryKey);

  const {
    data: flatTasksData,
    fetchNextPage: fetchNextPageFlat,
    hasNextPage: hasNextPageFlat,
    isLoading: isFlatTasksLoading,
    isFetching: isFlatTasksFetching,
    isFetched: isFlatTasksFetched,
  } = useInfiniteQuery({
    queryKey: [deferredFlatListQueryKey],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      graphneAPI.postGraphneTodoList({
        taskFilterDefinition: {
          skip: countOptionsFetchForSelect * pageParam,
          take: countOptionsFetchForSelect,
          data: filtersMapper(filters),
          simulationId: simulationId.id ? simulationId.id : undefined,
        },
      }),
    getNextPageParam: (lastPage, allPages) =>
      allPages.reduce((sum, page) => sum + (page.tasks?.length ?? 0), 0) < lastPage.total ? allPages.length : undefined,
    select: (data) => {
      return {
        tasks: data.pages.flatMap((page) => (page.tasks ? page.tasks : [])),
        total: data.pages[0]?.total,
      };
    },
    throwOnError: () => {
      displayToast({
        toastType: 'error',
        message: tWrapper('error'),
      });
      return false;
    },
    enabled: !isTreeViewEnabled,
  });

  const {
    data: treeTasksData,
    isLoading: isTreeTasksLoading,
    isFetching: isTreeTasksFetching,
    isFetched: isTreeTasksFetched,
    fetchNextPage: fetchNextPageTree,
    hasNextPage: hasNextPageTree,
  } = useInfiniteQuery({
    queryKey: [deferredTreeListQueryKey],
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      graphneAPI.postGraphneTodoTree({
        taskFilterDefinition: {
          skip: countOptionsFetchForSelect * pageParam,
          take: countOptionsFetchForSelect,
          data: filtersMapper(filters),
          simulationId: simulationId.id ? simulationId.id : undefined,
        },
      }),
    getNextPageParam: (lastPage, allPages) =>
      allPages.reduce((sum, page) => sum + (page.trees?.length ?? 0), 0) < lastPage.total ? allPages.length : undefined,
    select: (data) => {
      return {
        tasks: data.pages.flatMap((page) => (page.trees ? page.trees : [])),
        total: data.pages[0]?.total,
      };
    },
    throwOnError: () => {
      displayToast({
        toastType: 'error',
        message: tWrapper('error'),
      });
      return false;
    },
    enabled: isTreeViewEnabled,
  });

  const flatTasksList = useMemo(() => flatTasksData?.tasks ?? [], [flatTasksData]);
  const treeTasksList = useMemo(() => treeTasksData?.tasks ?? [], [treeTasksData]);
  const totalTasks = (isTreeViewEnabled ? treeTasksData?.total : flatTasksData?.total) ?? 0;

  const flatTaskListCheckMap = useFlatTasksListStore((state) => state.flatTaskListCheckMap);
  const onFlatTasksCheck = useFlatTasksListStore((state) => state.onCheck);
  const onFlatTasksCheckAll = useFlatTasksListStore((state) => state.onCheckAll);

  const treeTasksListCheckMap = useTreeTasksListStore((state) => state.treeTasksListCheckMap);
  const onTreeTasksCheck = useTreeTasksListStore((state) => state.onCheck);
  const onTreeTasksCheckAll = useTreeTasksListStore((state) => state.onCheckAll);

  const checkTasksIds = useMemo(() => {
    const checkMap = isTreeViewEnabled ? treeTasksListCheckMap : flatTaskListCheckMap;
    return [...checkMap].reduce((acc: number[], [taskId, value]) => {
      if (value.checked) {
        acc.push(Number(taskId));
      }
      return acc;
    }, []);
  }, [isTreeViewEnabled, treeTasksListCheckMap, flatTaskListCheckMap]);

  const handleTaskCheck = (taskId: string) => {
    if (isTreeViewEnabled) {
      onTreeTasksCheck(taskId);
    } else {
      onFlatTasksCheck(taskId);
    }
  };

  const handleCheckAll = useCallback(() => {
    if (isTreeViewEnabled) {
      onTreeTasksCheckAll();
    } else {
      onFlatTasksCheckAll();
    }
  }, [isTreeViewEnabled, onFlatTasksCheckAll, onTreeTasksCheckAll]);

  const onCheckAll = useCallback(
    (value: boolean) => {
      handleCheckAll();
      setIsCheckAllFilterChecked(value);
    },
    [handleCheckAll],
  );

  const commonViewProps = {
    checkTasksIds,
    totalTasks,
    handleTaskCheck,
    handleCheckAll,
    onCheckAll,
    isCheckAllFilterChecked,
  };

  if (isTreeViewEnabled) {
    return {
      isLoading: isTreeTasksLoading,
      isFetching: isTreeTasksFetching,
      isFetched: isTreeTasksFetched,
      tasksList: treeTasksList,
      hasNextPage: hasNextPageTree,
      fetchNextPage: fetchNextPageTree,
      taskViewType: TaskViewType.TREE,
      queryKey: treeListQueryKey,
      ...commonViewProps,
    };
  } else {
    return {
      isLoading: isFlatTasksLoading,
      isFetching: isFlatTasksFetching,
      isFetched: isFlatTasksFetched,
      tasksList: flatTasksList,
      hasNextPage: hasNextPageFlat,
      fetchNextPage: fetchNextPageFlat,
      taskViewType: TaskViewType.FLAT,
      queryKey: flatListQueryKey,
      ...commonViewProps,
    };
  }
};
